// @flow

import * as Sentry from '@sentry/react';

import get from 'lodash/get';
import min from 'lodash/min';

import type { Grid, Currency } from '@gandi/billing';
import type { Process, DomainPhase } from '@gandi/cart';

import type {
  AllPhasePrices,
  FormattedCreate,
  FormattedPrices,
  Phase,
  Price,
  Prices,
  Product,
  Tax,
  TaxedPrice,
} from '../../types';

interface DurationError extends Error {
  process: string;
  phase: string;
  discount: boolean;
  duration: number;
  pricesForProcess: Array<Price>;
  normalPrice?: boolean;
}
/**
 * Get the minimum duration available for a given set of prices, of a given process.
 */
const getMinDurationForProcess = (
  product: Product,
  {
    process,
    phase,
    discount = false,
  }: { process: Process, phase: DomainPhase, discount: boolean, ... },
): number => {
  const prices = product.prices;

  const discountDurations = prices
    .filter(
      price =>
        price.discount === discount &&
        (process !== 'create' || !phase || price.options.phase === phase),
    )
    .map(price => price.min_duration);

  if (discountDurations.length) {
    return min(discountDurations);
  }

  const allDurations = prices.map(price => price.min_duration);
  if (!allDurations.length) {
    // $FlowFixMe[prop-missing]
    const error: DurationError = new Error(
      `Could not find any durations for process ${process}`,
    );
    error.process = process;
    error.phase = phase;
    error.discount = discount;
    error.pricesForProcess = prices;

    Sentry.captureException(error, {
      fqdn: get(product, 'products.0.name', '???'),
      process,
      phase,
    });
  }

  return min(allDurations);
};

/**
 * Get the lowest price available for a given set of prices, of a given process.
 */
const getPriceForProcess = (
  product: Product,
  {
    process,
    phase,
    duration,
    discount = false,
    normalPrice,
  }: {
    process: Process,
    phase: DomainPhase,
    duration: number,
    discount?: boolean,
    normalPrice?: boolean,
    ...
  },
): TaxedPrice | null => {
  const pricesForProcess = product.prices;
  const prices = pricesForProcess.filter(
    price => process !== 'create' || !phase || price.options.phase === phase,
  );

  let foundPrice = prices.reduce(
    (previousFoundPrice, price) => {
      const beforeTaxes = previousFoundPrice?.beforeTaxes;

      if (
        price.discount === discount &&
        duration >= price.min_duration &&
        duration <= price.max_duration &&
        (beforeTaxes === undefined || price.price_before_taxes < beforeTaxes)
      ) {
        return (
          (normalPrice
            ? {
                afterTaxes:
                  price.normal_price_after_taxes || price.price_after_taxes,
                beforeTaxes:
                  price.normal_price_before_taxes || price.price_before_taxes,
              }
            : {
                afterTaxes: price.price_after_taxes,
                beforeTaxes: price.price_before_taxes,
              }) || null
        );
      }

      return previousFoundPrice;
    },
    (null: TaxedPrice | null),
  );

  /*
   * In some cases, filtering prices based on the discount value
   * makes it impossible to select a price, due to an API inconsistency.
   * In this case, select the price from non-filtered prices object.
   */
  if (foundPrice === null) {
    foundPrice = prices.reduce(
      (previousFoundPrice, current) => {
        if (
          duration >= current.min_duration &&
          duration <= current.max_duration
        ) {
          return {
            beforeTaxes:
              current.normal_price_before_taxes || current.price_before_taxes,
            afterTaxes:
              current.normal_price_after_taxes || current.price_after_taxes,
          };
        }

        return previousFoundPrice;
      },
      (null: TaxedPrice | null),
    );
  }

  if (foundPrice === null) {
    // $FlowFixMe[prop-missing]
    const error: DurationError = new Error(
      `Price could not be found for process ${process} and phase ${phase}`,
    );
    error.process = process;
    error.phase = phase;
    error.discount = discount;
    error.duration = duration;
    error.pricesForProcess = pricesForProcess;
    error.normalPrice = normalPrice;

    Sentry.captureException(error, {
      fqdn: get(product, 'products.0.name', '???'),
      process,
      phase,
    });
  }

  return foundPrice;
};

/**
 * Checks wether a given set of prices for a given process contains any discounts.
 * @param {Array}   pricesForProcess      prices structure of a given process, coming from the API
 * @param {Object}  options { process, phase, discount }
 */
const hasDiscountForProcess = (
  product: Product,
  { process, phase }: { process: Process, phase: DomainPhase | null, ... },
): boolean =>
  product.prices.filter(
    price =>
      price.discount === true &&
      (process === 'create' || !phase || price.options.phase === phase),
  ).length > 0;

function sortPhases(a: Partial<Phase>, b: Partial<Phase>) {
  const aStartsAt = a.starts_at;
  const bStartsAt = b.starts_at;
  if (!aStartsAt || !bStartsAt) {
    return 0;
  }
  return new Date(aStartsAt).valueOf() - new Date(bStartsAt).valueOf();
}

/**
 * Format the prices structure coming from the API,
 * into something more workable in the front-end.
 * This new structure is meant to be stored in the application's state.
 * @param {Object}   suggestion           a suggestion object containing API prices structure.
 */
export const formatSuggestionPrices = ({
  phase,
  prices,
}: {
  phase: DomainPhase,
  prices: Prices,
  ...
}): FormattedPrices => {
  const output: {
    currency: Currency,
    grid: Grid,
    taxes: Array<Tax>,
    prices: {
      ...AllPhasePrices,
    },
  } = {
    currency: prices.currency,
    grid: prices.grid,
    taxes: prices.taxes,
    prices: {},
  };

  type Products = {
    [string: Process]: Product,
  };

  const products = prices.products.reduce((acc, product) => {
    acc[product.process] = product;
    return acc;
  }, ({}: Products));

  if (products.create) {
    const phases = [
      ...new Set([...products.create.phases.map(({ name }) => name), phase]),
    ]
      .map(name => {
        const phaseObj = products.create.phases.find(
          ({ name: phaseName }) => phaseName === name,
        );

        return {
          name,
          starts_at: phaseObj?.starts_at,
          // Uncomment to force a phase starting in the future:
          // starts_at: '2038-01-19T03:14:07Z',
          ends_at: phaseObj?.ends_at,
        };
      })
      .sort(sortPhases);

    output.prices.create = phases.reduce((acc, currentPhase) => {
      const minDuration = getMinDurationForProcess(products.create, {
        phase: currentPhase.name,
        process: 'create',
        discount: false,
      });

      const price = getPriceForProcess(products.create, {
        phase: currentPhase.name,
        process: 'create',
        duration: minDuration,
        discount: false,
      });

      const hasDiscount = hasDiscountForProcess(products.create, {
        phase: currentPhase.name,
        process: 'create',
      });

      acc[currentPhase.name] = {
        minDuration,
        price,
      };

      if (hasDiscount) {
        const minDurationDiscount = getMinDurationForProcess(products.create, {
          phase: currentPhase.name,
          process: 'create',
          discount: true,
        });

        const discountPrice = getPriceForProcess(products.create, {
          phase: currentPhase.name,
          process: 'create',
          duration: minDurationDiscount,
          discount: true,
        });

        const normalPrice = getPriceForProcess(products.create, {
          phase: currentPhase.name,
          process: 'create',
          duration: minDurationDiscount,
          discount: true,
          normalPrice: true,
        });

        if (
          discountPrice !== null &&
          normalPrice !== null &&
          discountPrice !== normalPrice
        ) {
          const rebate = Math.round(
            ((normalPrice.beforeTaxes - discountPrice.beforeTaxes) /
              normalPrice.beforeTaxes) *
              100,
          );

          acc[currentPhase.name].discount = {
            minDuration: minDurationDiscount,
            price: discountPrice,
            normalPrice,
            rebate,
          };
        }
      }

      return acc;
    }, ({ phases }: FormattedCreate));
  }

  if (products.renew) {
    const minDuration = getMinDurationForProcess(products.renew, {
      phase,
      process: 'renew',
      discount: false,
    });

    const price = getPriceForProcess(products.renew, {
      phase,
      process: 'renew',
      duration: minDuration,
      discount: false,
    });

    const createPrice: null | TaxedPrice =
      (output.prices.create &&
        output.prices.create.golive &&
        (output.prices.create.golive.discount?.price ||
          output.prices.create.golive.price)) ||
      null;

    const priceDiff: ?number =
      (createPrice !== null &&
        price !== null &&
        createPrice.beforeTaxes - price.beforeTaxes) ||
      undefined;

    const cheap: boolean =
      createPrice !== null &&
      price !== null &&
      price.beforeTaxes / createPrice.beforeTaxes <= 0.75;

    output.prices.renew = {
      minDuration,
      price,
      priceDiff,
      cheap,
    };
  }

  return output;
};

export const addTaxedPrices = (
  taxedPrice1: TaxedPrice,
  taxedPrice2: TaxedPrice,
): TaxedPrice => ({
  afterTaxes: taxedPrice1.afterTaxes + taxedPrice2.afterTaxes,
  beforeTaxes: taxedPrice1.beforeTaxes + taxedPrice2.beforeTaxes,
});
